import * as React from "react";
import { Controller, useFormContext, RegisterOptions } from "react-hook-form";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface MAutocompleteProps<T> {
  name: string;
  options: T[];
  label: string;
  placeholder?: string;
  multiple?: boolean;
  filterKey?: keyof T;
  getOptionLabel?: (option: T) => string;
  validation?: RegisterOptions;
  disabled?: boolean;
  maxSelections?: number;
  helperText?: string;
  loading?: boolean;
  stylesInput?: React.CSSProperties;
  freeSolo?: boolean;
}

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "red",
  },
});

const MAutocomplete = <T,>({
  name,
  options,
  label,
  placeholder,
  multiple = false,
  filterKey,
  disabled,
  getOptionLabel = (option: T) => String(option),
  validation,
  maxSelections,
  helperText = "",
  loading = false,
  stylesInput,
  freeSolo = false,
}: MAutocompleteProps<T>) => {
  const { control } = useFormContext();

  const defaultFilter = createFilterOptions<T>({
    matchFrom: "any",
    stringify: (option: T) => getOptionLabel(option),
  });

  const filterOptions = (options: T[], state: { inputValue: string }) => {
    const filtered = defaultFilter(options, state);
    const inputValue = state.inputValue.toLowerCase();

    if (!inputValue) {
      return filtered;
    }

    // Ordenar para que los resultados que empiezan con el texto buscado aparezcan primero
    return filtered.sort((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      const startsWithA = labelA.startsWith(inputValue);
      const startsWithB = labelB.startsWith(inputValue);

      if (startsWithA && !startsWithB) return -1;
      if (!startsWithA && startsWithB) return 1;
      return 0;
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          multiple={multiple}
          options={options}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          disabled={disabled}
          freeSolo={freeSolo}
          style={stylesInput ? { ...stylesInput } : undefined}
          noOptionsText="No hay resultados"
          value={field.value ?? (multiple ? [] : null)}
          onChange={(_, data: any) => {
            if (multiple && maxSelections && data.length > maxSelections) {
              data = data.slice(0, maxSelections);
            }
            field.onChange(data);
          }}
          onInputChange={freeSolo ? (_, newInputValue) => {
            // En modo freeSolo, actualizar el valor cuando se escribe
            if (newInputValue !== field.value) {
              field.onChange(newInputValue);
            }
          } : undefined}
          isOptionEqualToValue={(option, value) => {
            if (freeSolo) {
              // En modo freeSolo, el valor puede ser un string directo
              const optionLabel = typeof option === 'string' ? option : getOptionLabel(option);
              const valueLabel = typeof value === 'string' ? value : getOptionLabel(value);
              return optionLabel === valueLabel;
            }
            return getOptionLabel(option) === getOptionLabel(value);
          }}
          disableCloseOnSelect={multiple}
          renderOption={(props, option, state) => {
            const { key, ...restProps } = props as any;
            const uniqueKey = `${getOptionLabel(option)}-${state.index}`;
            return (
              <li {...restProps} key={uniqueKey}>
                {multiple && (
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={state.selected}
                  />
                )}
                {getOptionLabel(option)}
              </li>
            );
          }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!fieldState.error}
              helperText={
                fieldState.error ? fieldState.error.message : helperText
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                classes: {
                  notchedOutline: fieldState.error ? "Mui-error" : "",
                },
              }}
            />
          )}
        />
      )}
    />
  );
};

export default MAutocomplete;
